<template>
  <div>
    <!-- Confirmation Modal -->
    <ConfirmationModal
      :title="confirmationTitle"
      :message="confirmationMessage"
      confirmColor="error darken-1"
      ref="confirmationModal"
    />

    <!-- Deduction Modal-->
    <v-dialog
      width="480px"
      :overlay-opacity="0.90"
      v-model="deductionDialog"
    >
      <v-card>
        <v-card-title class="d-block">
          <div class="font-weight-bold">{{item.id ? "Update" : "Add"}} Deduction</div>
          <p class="caption mb-4">Enter deduction details below.</p>
        </v-card-title>
        <v-card-text class="py-2">

          <v-form @submit.prevent="addItem()">
            <ValidationObserver ref="validationObserver">

              <ValidationProvider
                name="Deduction Type"
                :rules="'required'"
                v-slot="{ errors }"
              >
                <v-select
                  outlined
                  dense
                  :autofocus="true"
                  label="Select Deduction Type"
                  placeholder="Select deduction type from list"
                  :items="nonStatutoryDeductionTypes"
                  item-text="accountName"
                  item-value="accountName"
                  v-model="item.accountName"
                  :error-messages="errors"
                >
                  <template v-slot:label>
                    Select Deduction Type
                    <span class="red--text">*</span>
                  </template>
                </v-select>
              </ValidationProvider>

              <ValidationProvider
                name="Amount"
                :rules="'required|min_value:0|max_value:999999999999'"
                v-slot="{ errors }"
              >
                <vuetify-money
                  type="number"
                  outlined
                  hide-details
                  dense
                  label="Amount"
                  required
                  v-model="item.amount"
                  :error-messages="errors"
                  :options="VuetifyMoneyOptions"
                />
              </ValidationProvider>
            </ValidationObserver>

            <v-card-actions class="pt-5 justify-end">
              <v-btn
                @click="closeModal()"
                min-width="120"
                min-height="42"
                depressed
                class="text-capitalize"
              >Cancel</v-btn>
              <v-btn
                type="submit"
                min-width="120"
                min-height="42"
                depressed
                color="primary"
                class="text-capitalize"
              >Save</v-btn>
            </v-card-actions>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-row
      align="center"
      justify="space-between"
    >
      <v-col
        cols="12"
        md="5"
      >
        <h3>Deduction</h3>
      </v-col>
      <v-col
        cols="12"
        md="6"
        class="text-md-right"
      >
        <v-btn
          @click="showCalculator"
          class="text-capitalize mr-2"
          depressed
        >Tax Calculator</v-btn>
        <v-btn
          @click="openModal()"
          class="text-capitalize"
          depressed
          color="primary"
        >Add Deduction</v-btn>
      </v-col>
    </v-row>
    <!-- Table showing deduction data -->
    <v-card
      class="mt-5"
      outlined
      rounded="lg"
    >
      <v-data-table
        :items="deductions"
        hide-default-footer
        :headers="headers"
      >
        <!-- Customized No Data slot -->
        <template #no-data>
          <v-row justify="center">
            <v-col>
              <NoData />
              <p class="text-center mt-5">No Deduction Items</p>
            </v-col>
          </v-row>
        </template>
        
        <template #[`item.accountName`]="{ item }">
          <div class="my-4">
            <span class="text-capitalize">{{item.accountName}}</span>
          </div>
        </template>
        
        <template #[`item.amount`]="{ item }">
          {{asMoney(item.amount)}}
        </template>
        
        <template #[`item.actions`]="{ item }">
          <v-btn
            @click="openModal(item)"
            class="text-capitalize"
            text
            color="primary"
          >
            Edit</v-btn>
          <v-btn
            @click="removeItem(item)"
            class="text-capitalize"
            text
            color="error"
          >
            Remove</v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modal for calculator -->
    <v-dialog
      max-width="450"
      v-model="calcDialog"
    >
      <v-card rounded="lg">
        <v-card-title class="justify-space-between align-start">
          <div>
            <h3 class="text-h6 font-weight-medium">Tax Calculator</h3>
            <p class="text-subtitle-2 text--secondary"></p>
          </div>
        </v-card-title>

        <v-card-text>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th
                    v-for="(h, i) in calcHeaders"
                    :key="`calcHeader-${i}`"
                  >
                    {{ h }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(d, i) in calcData"
                  :key="`calcData-${i}`"
                >
                  <td>{{ d.accountName }}</td>
                  <td>{{ d.isFlat ? asMoney(d.rate) : asPercent(d.rate) }}</td>

                  <td>{{ asMoney(d.calculatedAmount) }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
        <v-card-actions class="mt-2 justify-end">
          <v-btn
            @click="calcDialog = false"
            min-width="120"
            depressed
            class="text-capitalize"
          >Cancel</v-btn>
          <v-btn
            :disabled="calcData.length == 0 || !totalTaxableIncome"
            @click="addAsDeduction()"
            min-width="120"
            depressed
            color="primary"
            class="text-capitalize"
          >Add as Deduction</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import PageMixin from '@/mixins/page.mixin';
import { VuetifyMoneyOptions } from '@/lib/constants/options';
import { cloneDeep } from 'lodash';
import { v4 } from 'uuid';

export default {
  mixins: [PageMixin],
  data() {
    return {
      VuetifyMoneyOptions: VuetifyMoneyOptions,
      calcDialog: false,
      deductionDialog: false,
      /** @type {import('vuetify').DataTableHeader[]}*/
      headers: [
        { text: 'Name', value: 'accountName', sortable: false },
        { text: 'Amount', value: 'amount', sortable: false },
        { text: 'Actions', value: 'actions', align: 'center', sortable: false }
      ],
      calcHeaders: ['Name', 'Rate', 'Amount'],
      calcData: [ ],
      item: {},
      confirmationTitle: "",
      confirmationMessage: "",
      totalStatutoryDeduction: 0,
    };
  },

  props: {
    totalTaxableIncome: {
      type: Number,
      default: 0,
    },
    totalIncome: {
      type: Number,
      default: 0,
    },
    deductions: {
      type: Array,
      default: () =>([])
    },
    nonStatutoryDeductionTypes: {
      type: Array,
      default: () =>([])
    },
    statutoryDeductionTypes: {
      type: Array,
      default: () =>([])
    } 
  },
  
  watch: {
    totalTaxableIncome: {
      handler: function () {
        this.calculateStatutoryDeductions();
      },
    },
  },

  methods: {
    showCalculator() {
      this.calculateStatutoryDeductions();
      this.calcDialog = true;
    },

    openModal(item) {
      this.$refs.validationObserver?.reset();
      this.item = cloneDeep(item) || {};
      this.deductionDialog = true;
    },

    closeModal(item) {
      this.deductionDialog = false;
      this.item = item || {};
      this.$refs.validationObserver?.reset();
    },

    async addItem() {
      const deductions = cloneDeep(this.deductions)
      const valid = await this.$refs.validationObserver?.validate();
      if(!valid) return;
      
      if(this.item.id) {
        const index = deductions.findIndex((el) => el.id == this.item.id)
        deductions.splice(index, 1, {...this.item});

        this.$emit('update', deductions) 

        this.closeModal();
        return;
      }
      this.item.id = v4(); 
      deductions.push(this.item);
      this.$emit('update', deductions) ;
      this.closeModal();
    },
  
    removeItem(item) {
      this.confirmationTitle = `Confirm Deletion`;
      this.confirmationMessage = `Are you sure you want to delete deduction type '${item.accountName}'?`;
      this.$refs.confirmationModal.show(async () => {
        const deductions = this.deductions.filter((el) => el.id !== item.id)
        this.$emit('update', deductions) 
      });
    },

    calculateStatutoryDeductions() {
      this.calcData = [];
      this.totalStatutoryDeduction = 0.0;

      this.statutoryDeductionTypes.forEach((el) => {
        el.calculatedAmount = this.calculateStatutoryDeduction(el);
        this.calcData.push(el)
        this.totalStatutoryDeduction += Number(el.calculatedAmount);
      });
  
      this.deductions.forEach((el) => {
        if (el.isTaxDeductible) {
          el.amount = this.totalStatutoryDeduction;
        }
      });
    },

    calculateStatutoryDeduction(deduction) {
      let total = 0;

      if (!deduction) return 0;
      
      const deductionTypes = this.statutoryDeductionTypes.concat(this.nonStatutoryDeductionTypes);
      for (let i = 0; i < deduction.dependencies.length; i++) {
        const find = deductionTypes.find(
          (el) => el && el.accountName.toLowerCase() === deduction.dependencies[i].toLowerCase()
        );
        const search = deductionTypes.find(
          (el) => el && el.accountName.toLowerCase() === deduction.dependencies[i].toLowerCase()
        );
        if (search) {
          search.amount = find ? find.amount : 0;
          total += this.calculateStatutoryDeduction(search);
        }
      }
      
      let amount = Number(this.totalTaxableIncome);
      switch (deduction.thresholdUsage) {
        case "Use Threshold if Taxable Income is Greater":
          amount =
            (Number(this.totalTaxableIncome) > Number(deduction.threshold)
              ? Number(deduction.threshold)
              : Number(this.totalTaxableIncome)) - Number(total);
          break;
        case "Use Threshold if Taxable Income is Less":
          amount =
            (Number(this.totalTaxableIncome) < Number(deduction.threshold)
              ? Number(deduction.threshold)
              : Number(this.totalTaxableIncome)) - Number(total);
          break;
        case "Use Threshold as a Deductible":
          amount =
            Number(this.totalTaxableIncome) -
            (total + Number(deduction.threshold));
          break;
      }
      amount = amount > 0 ? amount : 0;
      return deduction.isFlat
        ? Number(deduction.amount)
        : amount * (Number(deduction.rate) / 100);
    },


    // add the calculated value as a deduction
    addAsDeduction() {

      const deductions = cloneDeep(this.deductions)
       let index = deductions.findIndex(
        (el) => el.isTaxDeductible
      );
      const deductible = {
        id: v4(),
        accountName: "Total Tax Deduction",
        rate: "",
        isTaxDeductible: true,
        amount: this.totalStatutoryDeduction,
      };

      if (index > -1) {
        deductions.splice(index, 1, {...deductible});
      } else {
        deductions.push(deductible);
      }

      this.$emit('update', deductions) ;

      this.calcDialog = false;
      this.showSnack({
        color: 'success',
        title: `Deduction added`,
        message: `Deduction was added successfully`
      });
    }
  }
};
</script>
>
